import { Paper, Table, Checkbox, Flex, UnstyledButton } from '@mantine/core';

interface JurisdictionFund {
  id: number;
  name: string;
}

interface Jurisdiction {
  id: number;
  reporting_member_state: string;
  country_name: string;
  funds: JurisdictionFund[];
}

interface SelectedJurisdiction {
  reportingMemberState: string;
  xml: boolean;
  pdf: boolean;
  funds: number[];
}

interface JurisdictionsProps {
  jurisdictions?: Jurisdiction[];
  selectedJurisdictions?: SelectedJurisdiction[];
  onSelectJurisdiction: (reportingMemberState: string) => void;
  onSelectPDF: (reportingMemberState: string) => void;
  onSelectXML: (reportingMemberState: string) => void;
  onSelectAllJurisdictions: () => void;
  onSelectAllXML: () => void;
  onSelectAllPDF: () => void;
  onSelectFunds: (reportingMemberState: string) => void;
}

export const Jurisdictions = ({
  jurisdictions = [],
  onSelectJurisdiction,
  onSelectPDF,
  onSelectXML,
  selectedJurisdictions = [],
  onSelectAllJurisdictions,
  onSelectAllXML,
  onSelectAllPDF,
  onSelectFunds
}: JurisdictionsProps) => {
  const rows = jurisdictions.map((element: Jurisdiction) => {
    const selectedJurisdiction = selectedJurisdictions.find(
      item => item.reportingMemberState === element.reporting_member_state
    );
    return (
      <Table.Tr
        key={element.id}
        id={`export-drawer-jurisdiction-row-${element.reporting_member_state}`}
      >
        <Table.Td
          id={`export-drawer-jurisdiction-country-cell-${element.reporting_member_state}`}
          fz="14px"
          fw={600}
          lh="20px"
          c="#25262B"
          p="20px 12px"
        >
          <Flex
            id={`export-drawer-jurisdiction-country-row-${element.reporting_member_state}`}
            align="center"
            gap="8px"
          >
            <Checkbox
              id={`export-drawer-jurisdiction-country-checkbox-${element.reporting_member_state}`}
              size="xs"
              checked={!!selectedJurisdiction}
              onChange={() => onSelectJurisdiction(element.reporting_member_state)}
            />
            {element.country_name}
          </Flex>
        </Table.Td>
        <Table.Td id={`export-drawer-jurisdiction-xml-cell-${element.reporting_member_state}`}>
          <Flex
            id={`export-drawer-jurisdiction-xml-row-${element.reporting_member_state}`}
            justify="center"
          >
            <Checkbox
              id={`export-drawer-jurisdiction-xml-checkbox-${element.reporting_member_state}`}
              onChange={() => onSelectXML(element.reporting_member_state)}
              checked={!!selectedJurisdiction?.xml}
              disabled={!selectedJurisdiction}
              size="xs"
            />
          </Flex>
        </Table.Td>
        <Table.Td id={`export-drawer-jurisdiction-pdf-cell-${element.reporting_member_state}`}>
          <Flex
            id={`export-drawer-jurisdiction-pdf-row-${element.reporting_member_state}`}
            justify="center"
          >
            <Checkbox
              id={`export-drawer-jurisdiction-pdf-checkbox-${element.reporting_member_state}`}
              onChange={() => onSelectPDF(element.reporting_member_state)}
              disabled={!selectedJurisdiction}
              checked={!!selectedJurisdiction?.pdf}
              size="xs"
            />
          </Flex>
        </Table.Td>
        <Table.Td id={`export-drawer-jurisdiction-funds-cell-${element.reporting_member_state}`}>
          <Flex
            id={`export-drawer-jurisdiction-funds-row-${element.reporting_member_state}`}
            justify="center"
          >
            <UnstyledButton
              id={`export-drawer-jurisdiction-funds-button-${element.reporting_member_state}`}
              disabled={!selectedJurisdiction}
              fz="14px"
              td="underline"
              lh="20px"
              c="#868E96"
              onClick={() => onSelectFunds(element.reporting_member_state)}
            >
              {selectedJurisdiction
                ? element.funds.length === selectedJurisdiction.funds.length
                  ? `All ${selectedJurisdiction.funds.length} Fund(s) Selected`
                  : `${selectedJurisdiction.funds.length} Fund(s) Selected`
                : ''}
            </UnstyledButton>
          </Flex>
        </Table.Td>
      </Table.Tr>
    );
  });

  const selectedXMLs = selectedJurisdictions.filter(item => item.xml);
  const selectedPDFs = selectedJurisdictions.filter(item => item.pdf);

  return (
    // @ts-ignore
    <Paper
      id="export-drawer-jurisdictions-paper"
      withBorder
      radius="sm"
      p="0"
      sx={{ overflow: 'hidden' }}
    >
      <Table id="export-drawer-jurisdictions-table" withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th
              id="export-drawer-jurisdictions-header-country"
              p="10px 8px"
              c="gray.5"
              bg="#F8F9FA"
            >{`${jurisdictions.length} jurisdictions`}</Table.Th>
            <Table.Th
              id="export-drawer-jurisdictions-header-xml"
              ta="center"
              p="10px 8px"
              c="gray.5"
              bg="#F8F9FA"
            >
              XML
            </Table.Th>
            <Table.Th
              id="export-drawer-jurisdictions-header-pdf"
              ta="center"
              p="10px 8px"
              c="gray.5"
              bg="#F8F9FA"
            >
              PDF
            </Table.Th>
            <Table.Th
              id="export-drawer-jurisdictions-header-funds"
              ta="center"
              p="10px 8px"
              c="gray.5"
              bg="#F8F9FA"
            >
              Funds
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr id="export-drawer-jurisdictions-select-all-row">
            <Table.Td
              id="export-drawer-jurisdictions-select-all-cell"
              fz="14px"
              fw={600}
              lh="20px"
              c="#A6A7AB"
              p="20px 12px"
            >
              <Flex id="export-drawer-jurisdictions-select-all-content" align="center" gap="8px">
                <Checkbox
                  id="export-drawer-jurisdictions-select-all-checkbox"
                  checked={jurisdictions.length === selectedJurisdictions.length}
                  onChange={onSelectAllJurisdictions}
                  size="xs"
                />
                Select All
              </Flex>
            </Table.Td>
            <Table.Td id="export-drawer-jurisdictions-select-all-xml-cell">
              <Flex id="export-drawer-jurisdictions-select-all-xml-content" justify="center">
                <Checkbox
                  id="export-drawer-jurisdictions-select-all-xml-checkbox"
                  checked={
                    selectedJurisdictions.length > 0 &&
                    selectedXMLs.length === selectedJurisdictions.length
                  }
                  onChange={onSelectAllXML}
                  size="xs"
                />
              </Flex>
            </Table.Td>
            <Table.Td id="export-drawer-jurisdictions-select-all-pdf-cell">
              <Flex id="export-drawer-jurisdictions-select-all-pdf-content" justify="center">
                <Checkbox
                  id="export-drawer-jurisdictions-select-all-pdf-checkbox"
                  checked={
                    selectedJurisdictions.length > 0 &&
                    selectedPDFs.length === selectedJurisdictions.length
                  }
                  onChange={onSelectAllPDF}
                  size="xs"
                />
              </Flex>
            </Table.Td>
            <Table.Td id="export-drawer-jurisdictions-select-all-funds-cell"></Table.Td>
          </Table.Tr>
          {rows}
        </Table.Tbody>
      </Table>
    </Paper>
  );
};
